import GmAnalyticsAPI from 'dashboard/api/gmAnalytics';

console.log('🚀 GM Analytics Store: Initializing main analytics store...');
console.log('🔍 GM Analytics Store: GmAnalyticsAPI imported:', GmAnalyticsAPI);
console.log('🔍 GM Analytics Store: GmAnalyticsAPI type:', typeof GmAnalyticsAPI);
console.log('🔍 GM Analytics Store: GmAnalyticsAPI methods:', Object.getOwnPropertyNames(GmAnalyticsAPI));

import conversationAnalytics from './gm-conversation-analytics';
import sentimentAnalytics from './gm-sentiment-analytics';
import performanceAnalytics from './gm-performance-analytics';
import predictiveAnalytics from './gm-predictive-analytics';

console.log('📊 GM Analytics Store: Modules imported successfully');

const gmAnalyticsStore = {
  namespaced: true,
  
  state: {
    isLoading: false,
    error: null,
    lastUpdated: null,
    dashboardData: {
      kpis: {},
      conversationTrend: [],
      sentimentTrend: [],
      responseTimeTrend: [],
      channelPerformance: [],
      agentPerformanceMatrix: [],
      enhancedAgentMetrics: [],
      aiMessages: {},
      channels: [],
      agents: []
    },
    realTimeData: {},
    aiInsights: [],
    filters: {
      dateRange: {
        from: null,
        to: null,
        preset: 'last_30_days'
      },
      period: {
        type: 'month',
        value: null
      },
      channel: null,
      agent: null,
      channels: [],
      agents: [],
      teams: [],
      business_hours: false
    }
  },

  mutations: {
    SET_LOADING(state, isLoading) {
      console.log('🔄 GM Analytics Store: Setting loading state:', isLoading);
      state.isLoading = isLoading;
    },

    SET_ERROR(state, error) {
      console.log('❌ GM Analytics Store: Setting error:', error);
      state.error = error;
    },

    SET_LAST_UPDATED(state, timestamp) {
      console.log('⏰ GM Analytics Store: Setting last updated:', timestamp);
      state.lastUpdated = timestamp;
    },

    SET_DASHBOARD_DATA(state, data) {
      console.log('📊 GM Analytics Store: Setting dashboard data:', data);
      state.dashboardData = data;
    },

    SET_REAL_TIME_DATA(state, data) {
      console.log('🔄 GM Analytics Store: Setting real-time data:', data);
      state.realTimeData = data;
    },

    SET_AI_INSIGHTS(state, insights) {
      console.log('🤖 GM Analytics Store: Setting AI insights:', insights);
      state.aiInsights = insights;
    },

    SET_FILTERS(state, filters) {
      console.log('🔧 GM Analytics Store: Setting filters:', filters);
      state.filters = { ...state.filters, ...filters };
    },

    UPDATE_DATE_RANGE(state, dateRange) {
      console.log('📅 GM Analytics Store: Updating date range:', dateRange);
      state.filters.dateRange = { ...state.filters.dateRange, ...dateRange };
    },

    UPDATE_PERIOD(state, period) {
      console.log('📊 GM Analytics Store: Updating period:', period);
      state.filters.period = { ...state.filters.period, ...period };
    },

    UPDATE_CHANNEL(state, channel) {
      console.log('📱 GM Analytics Store: Updating channel:', channel);
      state.filters.channel = channel;
    },

    UPDATE_AGENT(state, agent) {
      console.log('👤 GM Analytics Store: Updating agent:', agent);
      state.filters.agent = agent;
    },

    CLEAR_ERROR(state) {
      console.log('✅ GM Analytics Store: Clearing error');
      state.error = null;
    },

    SET_AGENT_PERFORMANCE_MATRIX(state, data) {
      console.log('👥 GM Analytics Store: Setting agent performance matrix data:', data);
      state.dashboardData.agentPerformanceMatrix = data;
    },

    SET_ENHANCED_AGENT_METRICS(state, data) {
      console.log('📊 GM Analytics Store: Setting enhanced agent metrics data:', data);
      state.dashboardData.enhancedAgentMetrics = data;
    }
  },

  actions: {
    async fetchDashboardData({ commit, state }) {
      try {
        console.log('🔄 GM Analytics Store: Fetching dashboard data...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        console.log('🔍 GM Analytics Store: GmAnalyticsAPI object:', GmAnalyticsAPI);
        console.log('🔍 GM Analytics Store: getDashboard method:', GmAnalyticsAPI.getDashboard);
        console.log('🔍 GM Analytics Store: About to call getDashboard...');
        console.log('🔧 GM Analytics Store: Using filters:', state.filters);
        console.log('🔧 GM Analytics Store: Filters type:', typeof state.filters);
        console.log('🔧 GM Analytics Store: Filters keys:', Object.keys(state.filters));

        const response = await GmAnalyticsAPI.getDashboard(state.filters);
        console.log('✅ GM Analytics Store: Dashboard data fetched successfully:', response.data);
        console.log('🔍 GM Analytics Store: Full response object:', response);
        console.log('🔍 GM Analytics Store: Response status:', response.status);
        console.log('🔍 GM Analytics Store: Response data success:', response.data.success);
        console.log('🔍 GM Analytics Store: Response data data:', response.data.data);
        console.log('🔍 GM Analytics Store: Response filters:', response.data.filters);

        if (response.data.success) {
          commit('SET_DASHBOARD_DATA', response.data.data);
          commit('SET_LAST_UPDATED', response.data.timestamp);
          console.log('✅ GM Analytics Store: Dashboard data committed to store');
        } else {
          console.error('❌ GM Analytics Store: API returned success: false');
          throw new Error(response.data.error || 'Failed to fetch dashboard data');
        }

        return response.data;
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching dashboard data:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchRealTimeData({ commit }) {
      try {
        console.log('🔄 GM Analytics Store: Fetching real-time data...');
        commit('CLEAR_ERROR');

        const response = await GmAnalyticsAPI.getRealTimeData();
        console.log('✅ GM Analytics Store: Real-time data fetched successfully:', response.data);

        if (response.data.success) {
          commit('SET_REAL_TIME_DATA', response.data.data);
          commit('SET_LAST_UPDATED', response.data.timestamp);
        } else {
          throw new Error(response.data.error || 'Failed to fetch real-time data');
        }

        return response.data;
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching real-time data:', error);
        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    async fetchAIInsights({ commit }) {
      try {
        console.log('🤖 GM Analytics Store: Fetching AI insights...');
        commit('CLEAR_ERROR');

        const response = await GmAnalyticsAPI.getAIInsights();
        console.log('✅ GM Analytics Store: AI insights fetched successfully:', response.data);

        if (response.data.success) {
          commit('SET_AI_INSIGHTS', response.data.data);
          commit('SET_LAST_UPDATED', response.data.timestamp);
        } else {
          throw new Error(response.data.error || 'Failed to fetch AI insights');
        }

        return response.data;
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching AI insights:', error);
        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    async fetchConversationAnalytics({ commit }, filters = {}) {
      try {
        console.log('📊 GM Analytics Store: Fetching conversation analytics...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const response = await GmAnalyticsAPI.getConversationAnalytics(filters);
        console.log('✅ GM Analytics Store: Conversation analytics fetched successfully:', response.data);

        if (response.data.success) {
          commit('SET_LAST_UPDATED', response.data.timestamp);
        } else {
          throw new Error(response.data.error || 'Failed to fetch conversation analytics');
        }

        return response.data;
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching conversation analytics:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchSentimentAnalytics({ commit }, filters = {}) {
      try {
        console.log('😊 GM Analytics Store: Fetching sentiment analytics...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const response = await GmAnalyticsAPI.getSentimentAnalytics(filters);
        console.log('✅ GM Analytics Store: Sentiment analytics fetched successfully:', response.data);

        if (response.data.success) {
          commit('SET_LAST_UPDATED', response.data.timestamp);
        } else {
          throw new Error(response.data.error || 'Failed to fetch sentiment analytics');
        }

        return response.data;
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching sentiment analytics:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchPerformanceAnalytics({ commit }, filters = {}) {
      try {
        console.log('⚡ GM Analytics Store: Fetching performance analytics...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const response = await GmAnalyticsAPI.getPerformanceAnalytics(filters);
        console.log('✅ GM Analytics Store: Performance analytics fetched successfully:', response.data);

        if (response.data.success) {
          commit('SET_LAST_UPDATED', response.data.timestamp);
        } else {
          throw new Error(response.data.error || 'Failed to fetch performance analytics');
        }

        return response.data;
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching performance analytics:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    updateFilters({ commit }, eventData) {
      console.log('🔍 GM Analytics Store: Updating filters:', eventData);
      
      // Extract filters from event data if it contains chartType
      const filters = eventData.filters || eventData;
      commit('SET_FILTERS', filters);
    },

    updateDateRange({ commit }, dateRange) {
      console.log('📅 GM Analytics Store: Updating date range:', dateRange);
      commit('UPDATE_DATE_RANGE', dateRange);
    },

    updatePeriod({ commit }, period) {
      console.log('📊 GM Analytics Store: Updating period:', period);
      commit('UPDATE_PERIOD', period);
    },

    updateChannel({ commit }, channel) {
      console.log('📱 GM Analytics Store: Updating channel:', channel);
      commit('UPDATE_CHANNEL', channel);
    },

    updateAgent({ commit }, agent) {
      console.log('👤 GM Analytics Store: Updating agent:', agent);
      commit('UPDATE_AGENT', agent);
    },

    clearError({ commit }) {
      console.log('✅ GM Analytics Store: Clearing error');
      commit('CLEAR_ERROR');
    },

    async fetchAgentPerformanceMatrix({ commit }, filters = {}) {
      try {
        console.log('👥 GM Analytics Store: Fetching agent performance matrix...');
        console.log('🔍 GM Analytics Store: Filters:', filters);
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const response = await GmAnalyticsAPI.getAgentPerformanceMatrix(filters);
        console.log('✅ GM Analytics Store: Agent performance matrix response:', response);
        console.log('✅ GM Analytics Store: Response data:', response.data);

        if (response.data.success) {
          commit('SET_AGENT_PERFORMANCE_MATRIX', response.data.data);
          commit('SET_LAST_UPDATED', response.data.timestamp);
          console.log('✅ GM Analytics Store: Agent performance matrix data committed to store');
          return response.data.data;
        } else {
          console.error('❌ GM Analytics Store: API returned success: false');
          throw new Error(response.data.error || 'Failed to fetch agent performance matrix');
        }
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching agent performance matrix:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchEnhancedAgentMetrics({ commit }, filters = {}) {
      try {
        console.log('📊 GM Analytics Store: Fetching enhanced agent metrics...');
        console.log('🔍 GM Analytics Store: Filters:', filters);
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const response = await GmAnalyticsAPI.getEnhancedAgentMetrics(filters);
        console.log('✅ GM Analytics Store: Enhanced agent metrics response:', response);
        console.log('✅ GM Analytics Store: Response data:', response.data);

        if (response.data.success) {
          commit('SET_ENHANCED_AGENT_METRICS', response.data.data);
          commit('SET_LAST_UPDATED', response.data.timestamp);
          console.log('✅ GM Analytics Store: Enhanced agent metrics data committed to store');
          return response.data.data;
        } else {
          console.error('❌ GM Analytics Store: API returned success: false');
          throw new Error(response.data.error || 'Failed to fetch enhanced agent metrics');
        }
      } catch (error) {
        console.error('❌ GM Analytics Store: Error fetching enhanced agent metrics:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },

  getters: {
    getLoadingState: (state) => {
      console.log('📊 GM Analytics Store: Getting loading state:', state.isLoading);
      return state.isLoading;
    },

    getError: (state) => {
      console.log('📊 GM Analytics Store: Getting error:', state.error);
      return state.error;
    },

    getLastUpdated: (state) => {
      console.log('📊 GM Analytics Store: Getting last updated:', state.lastUpdated);
      return state.lastUpdated;
    },

    getDashboardData: (state) => {
      console.log('📊 GM Analytics Store: Getting dashboard data:', state.dashboardData);
      return state.dashboardData;
    },

    getRealTimeData: (state) => {
      console.log('📊 GM Analytics Store: Getting real-time data:', state.realTimeData);
      return state.realTimeData;
    },

    getAIInsights: (state) => {
      console.log('📊 GM Analytics Store: Getting AI insights:', state.aiInsights);
      return state.aiInsights;
    },

    getFilters: (state) => {
      console.log('📊 GM Analytics Store: Getting filters:', state.filters);
      return state.filters;
    },

    hasData: (state) => {
      const hasData = Object.keys(state.dashboardData).length > 0;
      console.log('📊 GM Analytics Store: Has data:', hasData);
      return hasData;
    },

    getAgentPerformanceMatrix: (state) => {
      console.log('👥 GM Analytics Store: Getting agent performance matrix:', state.dashboardData.agentPerformanceMatrix);
      return state.dashboardData.agentPerformanceMatrix;
    },

    getEnhancedAgentMetrics: (state) => {
      console.log('📊 GM Analytics Store: Getting enhanced agent metrics:', state.dashboardData.enhancedAgentMetrics);
      return state.dashboardData.enhancedAgentMetrics;
    }
  },

  modules: {
    conversationAnalytics,
    sentimentAnalytics,
    performanceAnalytics,
    predictiveAnalytics
  }
};

console.log('✅ GM Analytics Store: Main store created successfully');

export default gmAnalyticsStore;
